import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { store } from "../store";
import type { GrpcResponseMessage } from "./request-page.types";

// =============================================================================
// Types
// =============================================================================

type GrpcStreamEvent =
	| { type: "Message"; index: number; body_json: string }
	| { type: "Metadata"; entries: [string, string][] }
	| { type: "Trailers"; entries: [string, string][] }
	| { type: "Error"; message: string }
	| { type: "Complete"; status_code: number; status_message: string; duration_ms: number };

export type GrpcStreamState = {
	callId: string;
	documentId: string;
	messages: GrpcResponseMessage[];
	responseMetadata: [string, string][];
	trailers: [string, string][];
	statusCode: number | null;
	statusMessage: string | null;
	durationMs: number | null;
	isOpen: boolean;
	error: string | null;
};

// =============================================================================
// Atoms
// =============================================================================

/** Active gRPC stream states, keyed by callId. */
export const $grpcStreamState = atomFamily((callId: string) => {
	const streamAtom = atom(null as GrpcStreamState | null);
	streamAtom.debugLabel = `grpcStreamState::${callId}`;
	return streamAtom;
});

/** Set of active gRPC stream call IDs, keyed by documentId. */
export const $activeGrpcStreams = atomFamily((_documentId: string) => {
	const streamsAtom = atom(new Set<string>());
	streamsAtom.debugLabel = `activeGrpcStreams::${_documentId}`;
	return streamsAtom;
});

/** Map of callId -> unlisten function for cleanup. */
const streamUnlisteners = new Map<string, () => void>();

// =============================================================================
// Actions
// =============================================================================

/**
 * Start listening for events from a gRPC stream and update state.
 * Returns a cleanup function that unregisters the event listener.
 */
export const $startGrpcStreamListener = atom(
	null,
	async (
		get,
		set,
		params: {
			callId: string;
			documentId: string;
		}
	) => {
		const { callId, documentId } = params;

		// Initialize stream state
		set($grpcStreamState(callId), {
			callId,
			documentId,
			messages: [],
			responseMetadata: [],
			trailers: [],
			statusCode: null,
			statusMessage: null,
			durationMs: null,
			isOpen: true,
			error: null,
		});

		// Track the stream for document-level cleanup (immutable Set)
		set(
			$activeGrpcStreams(documentId),
			new Set([...get($activeGrpcStreams(documentId)), callId])
		);

		// Listen for Tauri events for this stream.
		// Use store.get/store.set (not the captured `get`/`set` from the write atom)
		// to always read the latest state. The captured `get` would be a stale closure
		// that misses intermediate updates when multiple events arrive quickly.
		const stateAtom = $grpcStreamState(callId);
		const eventName = `grpc:stream:${callId}`;
		const unlisten = await listen<GrpcStreamEvent>(eventName, (event) => {
			const streamEvent = event.payload;
			const current = store.get(stateAtom);
			if (!current) {
				return;
			}

			switch (streamEvent.type) {
				case "Message": {
					let parsedBody: unknown = streamEvent.body_json;
					try {
						parsedBody = JSON.parse(streamEvent.body_json);
					} catch {
						// Keep as string
					}
					const newMsg: GrpcResponseMessage = {
						index: streamEvent.index,
						body: parsedBody,
						bodySize: streamEvent.body_json.length,
					};
					store.set(stateAtom, {
						...current,
						messages: [...current.messages, newMsg],
					});
					break;
				}
				case "Metadata":
					store.set(stateAtom, {
						...current,
						responseMetadata: streamEvent.entries,
					});
					break;
				case "Trailers":
					store.set(stateAtom, {
						...current,
						trailers: streamEvent.entries,
					});
					break;
				case "Error":
					store.set(stateAtom, {
						...current,
						error: streamEvent.message,
						isOpen: false,
					});
					cleanupListener(callId);
					break;
				case "Complete":
					store.set(stateAtom, {
						...current,
						statusCode: streamEvent.status_code,
						statusMessage: streamEvent.status_message,
						durationMs: streamEvent.duration_ms,
						isOpen: false,
					});
					cleanupListener(callId);
					break;
			}
		});

		// Store the unlisten function for external cleanup
		streamUnlisteners.set(callId, unlisten);
	}
);
$startGrpcStreamListener.debugLabel = "startGrpcStreamListener";

/** Clean up a single stream's event listener. */
function cleanupListener(callId: string) {
	const unlisten = streamUnlisteners.get(callId);
	if (unlisten) {
		unlisten();
		streamUnlisteners.delete(callId);
	}
}

/** Send a message on an active gRPC stream. */
export const $grpcSendMessage = atom(
	null,
	async (_get, _set, params: { callId: string; message: string }) => {
		await invoke("grpc_stream_send", {
			input: { call_id: params.callId, message: params.message },
		});
	}
);
$grpcSendMessage.debugLabel = "grpcSendMessage";

/** End a gRPC stream (gracefully close or cancel). */
export const $grpcEndStream = atom(
	null,
	async (get, set, params: { callId: string; cancel?: boolean }) => {
		// Clean up event listener first
		cleanupListener(params.callId);

		await invoke("grpc_stream_end", {
			input: { call_id: params.callId, cancel: params.cancel ?? false },
		});

		// Clean up state (immutable Set) and remove atomFamily entry to prevent leaks
		const state = get($grpcStreamState(params.callId));
		if (state) {
			const activeStreams = get($activeGrpcStreams(state.documentId));
			const next = new Set(activeStreams);
			next.delete(params.callId);
			set($activeGrpcStreams(state.documentId), next);
		}
		$grpcStreamState.remove(params.callId);
	}
);
$grpcEndStream.debugLabel = "grpcEndStream";

/** Cancel all gRPC streams for a document (called on document close). */
export const $grpcCancelDocumentStreams = atom(null, async (get, set, documentId: string) => {
	// Clean up all event listeners and atomFamily entries for this document
	const activeStreams = get($activeGrpcStreams(documentId));
	for (const callId of activeStreams) {
		cleanupListener(callId);
		set($grpcStreamState(callId), null);
		$grpcStreamState.remove(callId);
	}

	await invoke("grpc_cancel_document_streams", { document_id: documentId });

	set($activeGrpcStreams(documentId), new Set());
	$activeGrpcStreams.remove(documentId);
});
$grpcCancelDocumentStreams.debugLabel = "grpcCancelDocumentStreams";
